/**
 * Match / turn / inning flow. Pure reducers over `MatchState` — given a shot's
 * event log they produce a `ShotResult` and advance the match. No rendering.
 */
import type { GameEvent } from '../types';
import { evaluateEventLog } from './evaluator';
import type { CaromRuleConfig, MatchConfig, MatchState, PlayerSetup, PlayerState, ShotEvaluation, ShotResult } from './types';

/** Korean handicap (countdown) mode: each carom knocks this many points off. */
export const HANDICAP_POINTS_PER_CAROM = 10;

/** Handicap countdown still to clear for a player (0 = cleared / has won). */
export function pointsRemaining(p: PlayerState): number {
  return Math.max(0, p.target - p.score * HANDICAP_POINTS_PER_CAROM);
}

/**
 * 30점/50점 players (beginner tiers) are exempt from the "마지막 3쿠션" finish
 * rule: clearing their handicap countdown wins outright, same as a plain
 * countdown match.
 */
function isExemptFromFinalShot(p: PlayerState): boolean {
  return p.target === 30 || p.target === 50;
}

/**
 * Korean 4-ball "마지막 3쿠션": true when this player has already cleared their
 * handicap countdown but has not yet sunk the required final-cushion carom, so
 * the game is not won. While true, foul penalties are suspended and only a
 * qualifying cushion carom ends the game.
 */
export function inFinalShotStage(config: MatchConfig, rule: CaromRuleConfig, p: PlayerState): boolean {
  return (
    config.mode === 'handicap' &&
    rule.finalShotMinCushions != null &&
    !isExemptFromFinalShot(p) &&
    p.target > 0 &&
    pointsRemaining(p) <= 0
  );
}

/**
 * The rule the shooter is actually playing to on THIS shot, which is not always
 * the match's rule.
 *
 * Once a player enters the 마지막 3쿠션 stage, a plain carom is worth nothing to
 * them AND passes the table (see `applyShotResult`) — only a carom with
 * `finalShotMinCushions` cushions can end the game. Anything choosing a shot (the
 * AI, a hint, a shot-quality readout) must therefore score the layout against a
 * raised `minCushions`, or it will happily pick the one shot that cannot win.
 */
export function shotGoalRule(config: MatchConfig, rule: CaromRuleConfig, p: PlayerState): CaromRuleConfig {
  if (!inFinalShotStage(config, rule, p)) return rule;
  return { ...rule, minCushions: rule.finalShotMinCushions! };
}

/**
 * True when EVERY remaining final-stage shot must bank before the first ball —
 * i.e. the house's outstanding bank quota has grown to equal the outstanding
 * qualifying-carom quota, so a non-bank carom can no longer finish the game.
 */
export function finalShotNeedsBank(config: MatchConfig, rule: CaromRuleConfig, p: PlayerState): boolean {
  if (!inFinalShotStage(config, rule, p)) return false;
  const needBanks = rule.finalBankCount ?? 0;
  if (needBanks <= 0) return false;
  const needTotal = Math.max(1, rule.finalShotCount ?? 1) + needBanks;
  return needBanks - p.finalBanks >= needTotal - p.finalCaroms;
}

function initPlayer(setup: PlayerSetup): PlayerState {
  const members = setup.members && setup.members.length ? setup.members : [setup.name];
  return {
    id: setup.id,
    name: setup.name,
    cueBall: setup.cueBall,
    isAI: setup.isAI,
    members,
    partnerTurn: 0,
    score: 0,
    finalCaroms: 0,
    finalBanks: 0,
    target: setup.startScore ?? 0,
    innings: 0,
    highRun: 0,
    currentRun: 0,
    fouls: 0,
    place: null,
  };
}

export function createMatch(config: MatchConfig): MatchState {
  const breaker = config.startingPlayerIndex ?? 0;
  return {
    config,
    players: config.players.map(initPlayer),
    activePlayerIndex: breaker,
    breakerIndex: breaker,
    inningNumber: 1,
    isBreakShot: true,
    phase: 'break',
    winnerIndex: null,
    history: [],
  };
}

/** Evaluate a shot for the active player; does not mutate state. */
export function evaluateShot(events: GameEvent[], state: MatchState): ShotResult {
  const playerIndex = state.activePlayerIndex;
  const striker = state.players[playerIndex].cueBall;
  const rule = state.config.rule;
  const evaluation = evaluateEventLog(events, striker, rule, state.isBreakShot);
  // A foul counts you backward by `foulPenalty` caroms (e.g. +10 on the countdown),
  // except in the final 3-cushion stage where fouls carry no penalty.
  const penalized = evaluation.foul && !inFinalShotStage(state.config, rule, state.players[playerIndex]);
  const appliedPoints = (evaluation.scored ? evaluation.points : 0) - (penalized ? rule.foulPenalty : 0);
  return { evaluation, playerIndex, inningNumber: state.inningNumber, appliedPoints };
}

/** The next team in the rotation (team-to-team, wrapping). */
const nextTeam = (i: number, n: number): number => (i + 1) % n;

/** The next team STILL PLAYING (place == null), wrapping. Falls back to `from`. */
function nextActive(players: PlayerState[], from: number): number {
  const n = players.length;
  for (let step = 1; step <= n; step++) {
    const idx = (from + step) % n;
    if (players[idx].place == null) return idx;
  }
  return from;
}

/** Apply a shot result, returning a new MatchState (input is not mutated). */
export function applyShotResult(state: MatchState, result: ShotResult): MatchState {
  const players: PlayerState[] = state.players.map((pl) => ({ ...pl }));
  const n = players.length;
  const config = state.config;
  const rule = config.rule;
  const p = players[result.playerIndex];
  const ev = result.evaluation;

  // --- update the shooting team's stats ---
  // Score may go negative so a foul penalty pushes the countdown back UP past the
  // starting handicap (e.g. a fresh foul: 100 remaining -> 110). `pointsRemaining`
  // clamps the displayed value, and a win still requires score*10 >= target.
  p.score = p.score + result.appliedPoints;
  if (ev.foul) p.fouls += 1;
  if (ev.scored) {
    p.currentRun += rule.pointsPerCarom;
    p.highRun = Math.max(p.highRun, p.currentRun);
  } else {
    p.innings += 1;
    p.currentRun = 0;
  }

  let activePlayerIndex = state.activePlayerIndex;
  let inningNumber = state.inningNumber;
  let phase = state.phase;
  let winnerIndex = state.winnerIndex;

  // Highest score wins; a tie at the top is a draw (null). For n=2 this is the
  // classic head-to-head comparison.
  const winnerByScore = (): number | null => {
    let best = -Infinity;
    let idx = -1;
    let tie = false;
    players.forEach((pl, i) => {
      if (pl.score > best) {
        best = pl.score;
        idx = i;
        tie = false;
      } else if (pl.score === best) {
        tie = true;
      }
    });
    return tie ? null : idx;
  };

  // Pass the table to the next team and advance the finishing team's partner so
  // the OTHER partner shoots when this team is next up (2v2 alternation).
  const passTurn = (): void => {
    p.partnerTurn = (p.partnerTurn + 1) % p.members.length;
    const from = activePlayerIndex;
    activePlayerIndex = nextActive(players, result.playerIndex); // skip finished players
    if (activePlayerIndex <= from) inningNumber += 1; // wrapped past the top of the order
    // Rotate the cue ball every time the table passes so no two consecutive turns
    // use the same ball. There are only two cue balls (white/yellow): for 2 teams
    // this reproduces the usual fixed assignment, but for an odd count (1v1v1) it
    // prevents the wrap-around double-white the per-seat assignment produced.
    players[activePlayerIndex].cueBall = p.cueBall === 'white' ? 'yellow' : 'white';
  };

  // 3+ player "play until last" (placement): a team that reaches its goal locks the
  // next place and drops out; the table passes to the next still-playing team. When
  // only one team remains it takes last place and the match ends. `winnerIndex` is
  // the FIRST finisher (place 1). Guarded to n >= 3; 1v1 keeps single-winner logic.
  const placeAndFinishIfLast = (idx: number): void => {
    const nextPlace = players.filter((pl) => pl.place != null).length + 1;
    players[idx].place = nextPlace;
    if (winnerIndex == null) winnerIndex = idx;
    const remaining = players.filter((pl) => pl.place == null);
    if (remaining.length <= 1) {
      if (remaining.length === 1) remaining[0].place = nextPlace + 1; // lone survivor = last
      phase = 'finished';
    } else {
      if (phase === 'break') phase = 'in-progress';
      activePlayerIndex = nextActive(players, idx); // hand the table on
    }
  };

  if (state.phase === 'equalizing') {
    // The non-breaker is taking their equalizing inning; resolve when it ends.
    if (!ev.scored) {
      if (players[0].score !== players[1].score) {
        phase = 'finished';
        winnerIndex = winnerByScore();
      } else {
        // tied after the equalizing inning -> sudden-death extra innings
        phase = 'sudden-death';
        activePlayerIndex = nextTeam(result.playerIndex, n);
        if (activePlayerIndex === state.breakerIndex) inningNumber += 1;
      }
    }
  } else if (state.phase === 'sudden-death') {
    // Alternate full innings; the first round that separates the scores decides it.
    if (!ev.scored) {
      activePlayerIndex = nextTeam(result.playerIndex, n);
      if (activePlayerIndex === state.breakerIndex) {
        inningNumber += 1;
        if (players[0].score !== players[1].score) {
          phase = 'finished';
          winnerIndex = winnerByScore();
        }
      }
    }
  } else {
    // --- normal turn handling: score keeps the table, miss/foul passes it ---
    if (!ev.scored) {
      if (config.solo) {
        inningNumber += 1; // solo: you keep the table; just count the inning
      } else {
        passTurn();
      }
    }

    // --- win / equalizing checks ---
    if (config.mode === 'race-to-points' && config.targetPoints != null) {
      const t = config.targetPoints;
      if (n >= 3) {
        // 3+ players: the shooter who reaches the target takes the next place and
        // drops out; play continues until one remains (last place).
        if (p.place == null && p.score >= t) placeAndFinishIfLast(result.playerIndex);
        else if (phase === 'break') phase = 'in-progress';
      } else {
        const reached = players.filter((pl) => pl.score >= t);
        if (reached.length > 0) {
          const reacher = winnerByScore();
          if (n === 2 && config.equalizingInning && reacher === state.breakerIndex) {
            // Breaker hit the target first; the opponent (one inning behind in an
            // alternating match) gets a tying inning before the match is decided.
            phase = 'equalizing';
            activePlayerIndex = nextTeam(reacher, n);
          } else {
            phase = 'finished';
            winnerIndex = winnerByScore();
          }
        } else if (phase === 'break') {
          phase = 'in-progress';
        }
      }
    } else if (config.mode === 'innings-limit' && config.inningsLimit != null) {
      if (players.every((pl) => pl.innings >= config.inningsLimit!)) {
        phase = 'finished';
        winnerIndex = winnerByScore();
      } else if (phase === 'break') {
        phase = 'in-progress';
      }
    } else if (config.mode === 'handicap') {
      if (rule.finalShotMinCushions != null && !isExemptFromFinalShot(p)) {
        // Korean 4-ball: clearing the countdown only opens the final stage. The
        // game is won when a team that was ALREADY cleared (before this shot) sinks
        // the required number of qualifying 3-cushion caroms (with the house bank
        // requirement met). The shot that clears the handicap therefore never wins
        // outright — it sets up the "마지막 3쿠션" finish that follows.
        const alreadyCleared = pointsRemaining(state.players[result.playerIndex]) <= 0;
        const needCaroms = Math.max(1, rule.finalShotCount ?? 1);
        const needBanks = rule.finalBankCount ?? 0;
        // `needCaroms` is a count of PLAIN qualifying caroms, on top of `needBanks`
        // bank ones — the two are separate quotas, not one quota that a bank shot
        // can double up for. So the total shots required is their sum: e.g. 1 carom
        // + 1 bank means exactly that, two distinct final shots, in either order —
        // never a single bank shot satisfying both at once.
        const needTotal = needCaroms + needBanks;
        const qualifies = ev.scored && ev.cushionsBeforeSecond >= rule.finalShotMinCushions;
        if (alreadyCleared && qualifies) {
          // A qualifying final carom: tally it (and whether it banked first), keep
          // the table, and win once enough total caroms — and enough of those banks
          // specifically — are made.
          p.finalCaroms += 1;
          if (ev.cushionsBeforeFirst >= 1) p.finalBanks += 1;
          if (p.finalCaroms >= needTotal && p.finalBanks >= needBanks) {
            if (n >= 3) placeAndFinishIfLast(result.playerIndex);
            else {
              phase = 'finished';
              winnerIndex = result.playerIndex;
            }
          } else if (phase === 'break') {
            phase = 'in-progress';
          }
        } else {
          // In the final stage a plain carom (the two reds without the required
          // cushions) is NOT a finishing shot AND does not keep the table — the
          // turn passes to the opponent, who gets their own chance to finish.
          if (alreadyCleared && ev.scored && !config.solo) passTurn();
          if (phase === 'break') phase = 'in-progress';
        }
      } else if (pointsRemaining(p) <= 0) {
        // Plain countdown: clearing wins. 3+ players play until last (placement).
        if (n >= 3) placeAndFinishIfLast(result.playerIndex);
        else {
          phase = 'finished';
          winnerIndex = result.playerIndex;
        }
      } else if (phase === 'break') {
        phase = 'in-progress';
      }
    } else if (phase === 'break') {
      phase = 'in-progress';
    }
  }

  return {
    ...state,
    players,
    activePlayerIndex,
    inningNumber,
    isBreakShot: false,
    phase,
    winnerIndex,
    history: [...state.history, result],
  };
}

export interface TimeoutResult {
  state: MatchState;
  /** True when this timeout was the shooter's THIRD in a row — they lose the
   *  match outright, regardless of score (see `applyTimeout`). */
  forfeited: boolean;
}

/**
 * A player let their shot clock run out: online play's per-turn timer treats
 * this exactly like a plain foul-miss (loses the table, takes the foul
 * penalty, cue ball rotates) but with no physics event log to evaluate — used
 * by `src/app/App.ts`'s turn timer. Three of these in a row FOR THE SAME
 * PLAYER (their own last three turns, not counting the opponent's turns in
 * between) forfeits the match to the opponent outright, overriding whatever
 * `applyShotResult` alone would have decided.
 */
export function applyTimeout(state: MatchState): TimeoutResult {
  const playerIndex = state.activePlayerIndex;
  const rule = state.config.rule;
  const evaluation: ShotEvaluation = {
    scored: false,
    points: 0,
    foul: true,
    endTurn: true,
    reason: 'timeout',
    firstObjectBall: null,
    secondObjectBall: null,
    cushionsBeforeSecond: 0,
    cushionsBetween: 0,
    cushionsBeforeFirst: 0,
    bothContacted: false,
  };
  // same foul-penalty rule as evaluateShot: suspended during the Korean 4-ball
  // final "마지막 3쿠션" stage.
  const penalized = !inFinalShotStage(state.config, rule, state.players[playerIndex]);
  const appliedPoints = -(penalized ? rule.foulPenalty : 0);
  const result: ShotResult = { evaluation, playerIndex, inningNumber: state.inningNumber, appliedPoints };
  let next = applyShotResult(state, result);

  const ownTurns = next.history.filter((r) => r.playerIndex === playerIndex);
  const lastThree = ownTurns.slice(-3);
  const forfeited = lastThree.length === 3 && lastThree.every((r) => r.evaluation.reason === 'timeout');
  if (forfeited) {
    const n = next.players.length;
    const players = next.players.map((pl) => ({ ...pl }));
    if (n <= 2) {
      // 1v1 (or solo, which never gets here — solo never passes the turn):
      // straight forfeit, same shape as reaching the target the normal way.
      next = { ...next, players, phase: 'finished', winnerIndex: n === 2 ? 1 - playerIndex : playerIndex };
    } else {
      // 3+ teams: knock this one out in the worst remaining place (mirrors
      // placeAndFinishIfLast's placement counting, from the bottom instead of
      // the top) and hand the table to whoever's still playing.
      const finishedCount = players.filter((pl) => pl.place != null).length;
      if (players[playerIndex].place == null) players[playerIndex].place = n - finishedCount;
      const remaining = players.filter((pl) => pl.place == null);
      let { activePlayerIndex, phase, winnerIndex } = next;
      if (remaining.length <= 1) {
        if (remaining.length === 1) remaining[0].place = players[playerIndex].place! - 1;
        phase = 'finished';
      } else if (activePlayerIndex === playerIndex) {
        activePlayerIndex = nextActive(players, playerIndex);
      }
      next = { ...next, players, activePlayerIndex, phase, winnerIndex };
    }
  }
  return { state: next, forfeited };
}

/** Lag: the player whose ball stops nearer the head rail shoots first. */
export function resolveLag(distA: number, distB: number): { firstPlayerIndex: 0 | 1 } {
  return { firstPlayerIndex: distA <= distB ? 0 : 1 };
}

/** Undo the last shot by replaying history from a fresh match. */
export function undoLastShot(state: MatchState): MatchState {
  if (state.history.length === 0) return state;
  const replay = state.history.slice(0, -1);
  let s = createMatch(state.config);
  for (const r of replay) s = applyShotResult(s, r);
  return s;
}
